import { BaseObjDao, ReportDao } from '@/dao';
import { Report, type ManagerReport } from '@/objects/generated';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useEventCommonStoreRef } from './eventCommonStore';
import { $tm } from '@/locize';

export type ManagerReportType =
    | 'ResultListClass'
    | 'StartList'
    | 'ResultListTotal'
    | 'StarterList'
    | 'StartClockFile'
    | 'ResultListOfficial'
    | 'ResultListUnofficial'
    | 'Heatplan'
    | 'Heatresult'
    | 'Heatstart'
    | 'CustomReport'
    | 'HeatplanTransfer';

export const useReportStore = defineStore('report', () => {
    const { Event } = useEventCommonStoreRef();

    /**
     * A loader for the managerReports
     */
    const managerReportLoader = ref(
        new LoaderWithReset<ManagerReport[]>({
            defaultValue: new Array<ManagerReport>(),
            loaderFunction: async () => {
                const managerReports = await ReportDao.ManagerReports();
                return managerReports;
            },
        }),
    );

    /**
     * A loader for the Reports
     */
    const reportLoader = ref(
        new LoaderWithReset<Report[]>({
            defaultValue: new Array<Report>(),
            loaderFunction: async () => {
                const Reports = await BaseObjDao.Load<Report>(Report);
                return Reports;
            },
        }),
    );

    // trigger load
    managerReportLoader.value.Load();
    reportLoader.value.Load();

    const managerReports = computed(() => managerReportLoader.value.Value);
    const reports = computed(() => reportLoader.value.Value);

    const reportsIncludedInManagerReports = computed(() => {
        const mgrReportIDs = new Set(managerReports.value.map((mr) => mr.ReportID));
        return reports.value.filter((r) => mgrReportIDs.has(r.ID));
    });

    const reportsWithGroupingKey = computed(() => {
        const rep = reportsIncludedInManagerReports.value.filter((r) => r.Groupingkey != null && r.Groupingkey!.length > 0);
        rep.sort((a, b) => {
            // We sort them by grouping key, if grouping key is the same we we sort them by name
            if (a.Groupingkey! < b.Groupingkey!) {
                return -1;
            } else if (a.Groupingkey! > b.Groupingkey!) {
                return 1;
            }

            return $tm(a.DescriptionKey ?? '')?.localeCompare($tm(b.DescriptionKey ?? '')) ?? 0;
        });
        return rep;
    });

    const groupedReports = computed(() => {
        const grouped = reportsWithGroupingKey.value.reduce(
            (acc, report) => {
                if (!acc[report.Groupingkey!]) {
                    acc[report.Groupingkey!] = [];
                }
                acc[report.Groupingkey!].push(report);
                return acc;
            },
            {} as Record<string, Report[]>,
        );

        return grouped;
    });

    const managerReportsForEvent = computed(() => {
        // we want the reports that has same sport, sportslevel and discipline as the event
        // if there is no specified we will include the ones with null in the respective fields
        const list = managerReports.value.filter((mr) => {
            return (
                (mr.SportID === null || mr.SportID === Event.value?.SportID) &&
                (mr.SportsLevelID === null || mr.SportsLevelID === Event.value?.SportlevelID) &&
                (mr.DisciplinID === null || mr.DisciplinID === Event.value?.DisciplineID)
            );
        });

        // order them by how specific they are, prioritize ones with specific sportslevel, then discipline then sport
        list.sort((a, b) => {
            const aScore = (a.SportsLevelID !== null ? 4 : 0) + (a.DisciplinID !== null ? 2 : 0) + (a.SportID !== null ? 1 : 0);
            const bScore = (b.SportsLevelID !== null ? 4 : 0) + (b.DisciplinID !== null ? 2 : 0) + (b.SportID !== null ? 1 : 0);
            return bScore - aScore;
        });

        return list;
    });

    return {
        ManagerReports: managerReports,
        ManagerReportsForEvent: managerReportsForEvent,
        Reports: reports,
        GroupedReports: groupedReports,
        ReportsWithGroupingKey: reportsWithGroupingKey,
        EnsureLoaded: async () => {
            await Promise.all([managerReportLoader.value.Load(), reportLoader.value.Load()]);
        },
    };
});

export const useReportStoreRef = () => {
    return storeToRefs(useReportStore());
};
